import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatDate } from "@/lib/format";
import { IDEA_IMAGE_TYPES, normalizeUrl, parseVimeoId } from "@/lib/tilbudsarbeidsrom/idea-board";
import type { IdeaCardPatch } from "@/hooks/use-offer-idea-cards";
import type { DBOfferIdeaCard } from "@/lib/db-types";

interface IdeaCardBodyProps {
  card: DBOfferIdeaCard;
  imageUrl: string | undefined;
  readOnly: boolean;
  uploading: boolean;
  onPatch: (patch: IdeaCardPatch) => void;
  onUpload: (file: File) => void;
}

const PLACEHOLDER: Record<DBOfferIdeaCard["kind"], string> = {
  note: "Skriv en idé …",
  image: "Bildetekst",
  link: "Hva viser lenken?",
  film: "Hva skal vi låne fra filmen?",
  question: "Hva må avklares?",
  excerpt: "",
};

/** Tekstfelt som lagrer når det forlates, og følger med når kortet endres utenfra. */
function CommitField({
  value, onCommit, readOnly, label, placeholder, multiline = true,
}: {
  value: string; onCommit: (next: string) => void; readOnly: boolean; label: string; placeholder: string; multiline?: boolean;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => {
    if (draft !== value) onCommit(draft);
  };
  const common = {
    value: draft,
    readOnly,
    placeholder,
    "aria-label": label,
    onChange: (e: { target: { value: string } }) => setDraft(e.target.value),
    onBlur: commit,
  };
  return multiline ? (
    <Textarea {...common} rows={3} className="text-sm resize-none min-h-0 border-transparent bg-transparent px-1 focus-visible:bg-background" />
  ) : (
    <Input {...common} className="h-8 text-sm" />
  );
}

/** Innholdet i et Idé-kort, etter korttype (VIB-2980). */
export function IdeaCardBody({ card, imageUrl, readOnly, uploading, onPatch, onUpload }: IdeaCardBodyProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const bodyField = (
    <CommitField
      value={card.body}
      onCommit={(body) => onPatch({ body })}
      readOnly={readOnly}
      label="Tekst på kortet"
      placeholder={PLACEHOLDER[card.kind]}
    />
  );

  switch (card.kind) {
    case "image":
      return (
        <div className="flex flex-col gap-2">
          {card.storage_path ? (
            imageUrl ? (
              <img src={imageUrl} alt={card.body || "Bilde på idétavlen"} className="w-full rounded-sm object-cover max-h-56" draggable={false} />
            ) : (
              <div className="h-32 bg-muted rounded-sm flex items-center justify-center">
                <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            )
          ) : (
            !readOnly && (
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
                <SrIcon name={uploading ? "loader2" : "upload"} className={uploading ? "w-4 h-4 animate-spin" : "w-4 h-4"} />
                {uploading ? "Laster opp …" : "Velg bilde"}
              </Button>
            )
          )}
          <input
            ref={fileRef}
            type="file"
            accept={Object.keys(IDEA_IMAGE_TYPES).join(",")}
            className="sr-only"
            aria-label="Velg bilde til kortet"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUpload(file);
              e.target.value = "";
            }}
          />
          {bodyField}
        </div>
      );
    case "link":
      return (
        <div className="flex flex-col gap-2">
          {!readOnly && (
            <CommitField
              value={card.url ?? ""}
              onCommit={(url) => onPatch({ url: normalizeUrl(url) })}
              readOnly={readOnly}
              label="Lenke"
              placeholder="https://"
              multiline={false}
            />
          )}
          {card.url && (
            <a href={card.url} target="_blank" rel="noopener noreferrer" className="text-sm underline break-all">
              {card.url}
            </a>
          )}
          {bodyField}
        </div>
      );
    case "film":
      return (
        <div className="flex flex-col gap-2">
          {!readOnly && (
            <CommitField
              value={card.vimeo_id ? `https://vimeo.com/${card.vimeo_id}` : ""}
              onCommit={(v) => onPatch({ vimeo_id: parseVimeoId(v) })}
              readOnly={readOnly}
              label="Vimeo-lenke"
              placeholder="Vimeo-lenke eller id"
              multiline={false}
            />
          )}
          {card.vimeo_id && (
            <a
              href={`https://vimeo.com/${card.vimeo_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm underline inline-flex items-center gap-1.5"
            >
              <SrIcon name="film" className="w-3.5 h-3.5" /> Se filmen på Vimeo
            </a>
          )}
          {bodyField}
        </div>
      );
    case "excerpt":
      return (
        <figure className="flex flex-col gap-2">
          <blockquote className="border-l-2 border-foreground/30 pl-2 text-sm whitespace-pre-line line-clamp-[10]">
            {card.body}
          </blockquote>
          <figcaption className="text-[11px] text-muted-foreground">
            {card.source_label ?? "Kundekortet"}
            {card.source_date ? ` · ${formatDate(card.source_date)}` : ""}
          </figcaption>
        </figure>
      );
    default:
      return bodyField;
  }
}
